import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Badge } from '@/types'
import { businessService, BadgeResult } from '@/services/businessService'

// Category display info
const CATEGORY_INFO: Record<string, { label: string; icon: string }> = {
  data_profile: { label: 'Data & Profile', icon: '📋' },
  score_performance: { label: 'Score & Performance', icon: '📈' },
  verification_trust: { label: 'Verification & Trust', icon: '✅' },
  ai_model: { label: 'AI & Model Capability', icon: '🤖' },
  other: { label: 'Other', icon: '🏷️' },
}

// Category display order
const CATEGORY_ORDER = ['data_profile', 'score_performance', 'verification_trust', 'ai_model', 'other']

export function BadgesPage() {
  const { user } = useAuth()
  const [badges, setBadges] = useState<Badge[]>([])
  const [badgeCategories, setBadgeCategories] = useState<Record<string, Badge[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'earned' | 'available'>('all')
  const [sortBy, setSortBy] = useState<'name' | 'earned_date' | 'category'>('category')

  useEffect(() => {
    const fetchBadges = async () => {
      if (!user?.businessId) {
        setLoading(false)
        setError('No business linked to your account.')
        return
      }

      setLoading(true)
      setError(null)
      try {
        const response = await businessService.getBadges(user.businessId)

        // Transform API response to Badge[] for display
        const mappedBadges: Badge[] = response.badges.map((b: BadgeResult) => ({
          id: b.badge_id,
          name: b.name,
          description: b.description,
          icon: b.icon,
          color: b.color,
          criteria: { type: 'custom' as const, requirements: { category: b.category } },
          earnedAt: b.earned && b.earned_at ? b.earned_at : undefined,
        }))

        setBadges(mappedBadges)

        // Group by category
        const grouped: Record<string, Badge[]> = {}
        response.badges.forEach((b: BadgeResult, i: number) => {
          const cat = b.category || 'other'
          if (!grouped[cat]) grouped[cat] = []
          grouped[cat].push(mappedBadges[i])
        })
        setBadgeCategories(grouped)
      } catch (err: any) {
        console.error('Failed to fetch badges', err)
        setError('Failed to load badges. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchBadges()
  }, [user?.businessId])

  const earnedBadges = badges.filter(badge => badge.earnedAt)
  const availableBadges = badges.filter(badge => !badge.earnedAt)

  const filteredBadges = filter === 'earned'
    ? earnedBadges
    : filter === 'available'
    ? availableBadges
    : badges

  const sortedBadges = [...filteredBadges].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'earned_date':
        if (a.earnedAt && b.earnedAt) {
          return new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime()
        }
        if (a.earnedAt && !b.earnedAt) return -1
        if (!a.earnedAt && b.earnedAt) return 1
        return a.name.localeCompare(b.name)
      case 'category':
        const catA = a.criteria.requirements?.category || 'other'
        const catB = b.criteria.requirements?.category || 'other'
        const orderA = CATEGORY_ORDER.indexOf(catA)
        const orderB = CATEGORY_ORDER.indexOf(catB)
        if (orderA !== orderB) return orderA - orderB
        // Within same category, earned first
        if (a.earnedAt && !b.earnedAt) return -1
        if (!a.earnedAt && b.earnedAt) return 1
        return a.name.localeCompare(b.name)
      default:
        return 0
    }
  })

  const earnedCount = earnedBadges.length
  const totalCount = badges.length
  const completionPercentage = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center py-8">
            <div className="text-4xl mb-4">⚠️</div>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Business Badges
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Achievements earned based on your business data and performance
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              {earnedCount}/{totalCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Badges Earned
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🏆</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {earnedCount}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  Badges Earned
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🎯</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {availableBadges.length}
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  Available to Earn
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📈</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  {completionPercentage}%
                </div>
                <div className="text-sm text-purple-600 dark:text-purple-400">
                  Completion Rate
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600 dark:text-gray-400">Overall Progress</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {completionPercentage}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-700"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Filter Tabs */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setFilter('earned')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'earned'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Earned ({earnedCount})
            </button>
            <button
              onClick={() => setFilter('available')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'available'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Available ({availableBadges.length})
            </button>
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Sort by:
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="category">Category</option>
              <option value="earned_date">Earned Date</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Badges Grid — Grouped by Category */}
      {sortBy === 'category' ? (
        // Render grouped by category
        <div className="space-y-6">
          {CATEGORY_ORDER.map(catKey => {
            const catBadges = (badgeCategories[catKey] || []).filter(b => {
              if (filter === 'earned') return !!b.earnedAt
              if (filter === 'available') return !b.earnedAt
              return true
            })
            if (catBadges.length === 0) return null
            const info = CATEGORY_INFO[catKey] || CATEGORY_INFO.other
            return (
              <div key={catKey} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span>{info.icon}</span>
                  {info.label}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                    ({catBadges.filter(b => b.earnedAt).length}/{catBadges.length})
                  </span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {catBadges.map(badge => (
                    <BadgeCard key={badge.id} badge={badge} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        // Flat sorted list
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedBadges.map(badge => (
              <BadgeCard key={badge.id} badge={badge} />
            ))}
          </div>
        </div>
      )}

      {/* Achievement Tips */}
      {availableBadges.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">💡</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Tips to Earn More Badges
              </h3>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Complete all fields in your business profile for "Profile Complete"</li>
                <li>• Add turnover, employee count, industry, and type for "Data Champion"</li>
                <li>• Upload 3+ financial year records for "Financial Historian"</li>
                <li>• Upload 3+ documents for "Document Keeper"</li>
                <li>• Set all compliance filings to up-to-date for "Compliance Ready"</li>
                <li>• Generate your AI score to unlock score-based badges</li>
                <li>• Run all 5 AI analyses for "Deep Analysis"</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Individual Badge Card component
function BadgeCard({ badge }: { badge: Badge }) {
  const isEarned = !!badge.earnedAt

  return (
    <div
      className={`relative rounded-lg border p-4 transition-shadow hover:shadow-md ${
        isEarned
          ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/10 opacity-70'
      }`}
    >
      {/* Earned indicator */}
      {isEarned && (
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center justify-center w-5 h-5 bg-green-500 rounded-full">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </div>
      )}

      <div className="flex items-start space-x-3">
        <div className={`text-3xl flex-shrink-0 ${!isEarned ? 'grayscale' : ''}`}>
          {badge.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold truncate ${
            isEarned
              ? 'text-gray-900 dark:text-white'
              : 'text-gray-500 dark:text-gray-400'
          }`}>
            {badge.name}
          </h3>
          <p className={`text-xs mt-1 line-clamp-2 ${
            isEarned
              ? 'text-gray-600 dark:text-gray-300'
              : 'text-gray-400 dark:text-gray-500'
          }`}>
            {badge.description}
          </p>
          <div className="mt-2">
            {isEarned ? (
              <span className="inline-flex items-center text-xs text-green-700 dark:text-green-400">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Earned {badge.earnedAt ? new Date(badge.earnedAt).toLocaleDateString() : ''}
              </span>
            ) : (
              <span className="inline-flex items-center text-xs text-gray-400 dark:text-gray-500">
                Not Earned
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
